import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { logout, selectIsRememberMe, selectUserAuthStatus } from '../../features/auth/auth.slice';
import useComponentVisible from '../../hooks/useComponentVisible';
import { useWindowSize } from '../../hooks/useWindowSize';
import { LogoIcon } from '../common/icons/logo.icon';

type Props = {
  theme?: string;
};

function SiteNavbarComponent({ theme }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthentication = useAppSelector(selectUserAuthStatus);

  const isRememberMe = useAppSelector(selectIsRememberMe);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!isRememberMe) {
      dispatch(logout());
    }
  }, [isRememberMe]);

  const [menuOpen, setMenuOpen] = useState(false);
  const { ref, isComponentVisible } = useComponentVisible(menuOpen);
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [isComponentVisible]);

  const handleClose = () => setMenuOpen(false);

  const [width, height] = useWindowSize();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    // Attach the event listener when the component mounts
    window.addEventListener('scroll', handleScroll);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav className={`navbar  ${theme == 'light' ? 'bg-eightColor' : 'bg-firstColor'} tw-z-[200] tw-relative`}>
      <Container fluid="xxl" className="justify-content-center">
        {width < 1000 && (
          <Row className="w-100 align-items-center tw-relative ">
            <Col sm="6" xs="6" className="d-flex align-items-center p-0 color-twelveColor gap-1">
              <Link to={`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}`} className="tw-flex tw-items-center tw-gap-1">
                <LogoIcon theme={theme == 'light' ? 'blue' : 'white'} />
                <span className="font-sofiaSans fw-bold fs-4">LouiseBot</span>
              </Link>
            </Col>
          </Row>
        )}

        {/* header tablet and web size */}
        {width >= 1000 && (
          <Row className="w-100 align-items-center d-lg-flex d-md-flex d-sm-flex">
            <Col lg="8" md="8" sm="8" className="d-flex align-items-center gap-4 p-0">
              <Link to="." className="tw-flex tw-items-center tw-gap-2">
                <LogoIcon theme={theme == 'light' ? 'blue' : 'white'} />
                <span className="font-sofiaSans fw-bold fs-4 color-twelveColor">LouiseBot</span>
              </Link>
            </Col>
          </Row>
        )}
      </Container>

      {menuOpen && (
        <>
          <div ref={ref} className="tw-absolute tw-top-16 h-100 w-100 tw-h-full   ">
            <div className="tw-flex tw-flex-col tw-bg-[#19223F]  w-100 tw-z-[1041] tw-relative">
              <div className="tw-py-3 tw-px-5 tw-border-solid  tw-border-t-0 tw-border-s-0 tw-border-e-0 tw-border-b-[1px] tw-border-[#1D284E]">
                <button
                  type="button"
                  className="color-sevenColor font-sofiaSans fs-4 tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer"
                  onClick={() => {
                    handleClose();
                    navigate(`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}`);
                  }}
                >
                  {t('SiteNavbar/l-05')}
                </button>
              </div>
              <div className="tw-py-3 tw-p-5 tw-border-solid  tw-border-t-0 tw-border-s-0 tw-border-e-0 tw-border-b-[1px] tw-border-[#1D284E]">
                <button
                  type="button"
                  className="color-sevenColor font-sofiaSans fs-4 tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer"
                  onClick={() => {
                    handleClose();
                    navigate(`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}/plans`);
                  }}
                >
                  {t('SiteNavbar/l-03')}
                </button>
              </div>
              <div className="tw-py-3 tw-p-5 tw-border-solid  tw-border-t-0 tw-border-s-0 tw-border-e-0 tw-border-b-[1px] tw-border-[#1D284E]">
                <button
                  type="button"
                  className="color-sevenColor font-sofiaSans fs-4 tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer"
                  onClick={() => {
                    handleClose();
                    navigate(`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}/human-clone-page`);
                  }}
                >
                  {t('SiteNavbar/l-06')}
                </button>
              </div>
              <div className="tw-p-5">
                <button
                  type="button"
                  className="nav-btn w-100 sofiaSans tw-border-none tw-cursor-pointer"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(`${window.location.origin}/${MultiLanguageUrl[i18n.language] || 'en-US'}/plans`);
                  }}
                >
                  {t('SiteNavbar/l-04')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

export default WrappedErrorBoundary(SiteNavbarComponent);
